import { WorkflowId } from '@ply-io/ply-front-shared-utilities';

import { dispatchTask, getTask } from './api';
import {
	ProcessTaskResponse,
	ProcessTaskResponseInfo,
	SuccessProcessTaskResponse,
	Tasks,
	isErrorProcessTaskResponse,
	isFinalizeProcessTaskResponse,
	isFinalizedStatus,
	isSuccessProcessTaskResponse,
} from './types';
import { getWorkflow } from '../workflows';
import { TaskProcessError } from '@/utils/error-handling-service';

interface GetWorkflowCbPayload extends Record<string, unknown> {
	workflow_id: WorkflowId;
}

const isGetWorkflowCbPayload = (payload: ProcessTaskResponseInfo): payload is GetWorkflowCbPayload =>
	'workflow_id' in payload && typeof payload.workflow_id === 'number';
interface GetWorkflowDuplicateCbPayload extends Record<string, unknown> {
	new_workflow_id: WorkflowId;
}

const isGetWorkflowDuplicateCbPayload = (payload: ProcessTaskResponseInfo): payload is GetWorkflowDuplicateCbPayload =>
	'new_workflow_id' in payload && typeof payload.new_workflow_id === 'number';

interface GetInstallInstallableCbPayload extends Record<string, unknown> {
	new_workflow_id: WorkflowId;
}

const isGetInstallInstallableCbPayload = (payload: ProcessTaskResponseInfo): payload is GetInstallInstallableCbPayload =>
	'new_workflow_id' in payload && typeof payload.new_workflow_id === 'number';

interface GetCreateSubflowFromNodesCbPayload extends Record<string, unknown> {
	new_workflow_id: WorkflowId;
	old_workflow_id: WorkflowId;
}

const isGetCreateSubflowFromNodesCbPayload = (payload: ProcessTaskResponseInfo): payload is GetCreateSubflowFromNodesCbPayload =>
	'new_workflow_id' in payload &&
	'old_workflow_id' in payload &&
	typeof payload.new_workflow_id === 'number' &&
	typeof payload.old_workflow_id === 'number';

const getWorkflowCb = (payload: ProcessTaskResponseInfo) => {
	if (!isGetWorkflowCbPayload(payload)) {
		throw new Error('Failed to get the publish workflow process status');
	}
	const { workflow_id: workflowId } = payload;
	return getWorkflow(workflowId);
};

const GET_ENTITIES_CB: Record<Tasks, (payload: SuccessProcessTaskResponse['info']) => Promise<unknown>> = {
	[Tasks.PublishWorkflow]: getWorkflowCb,
	[Tasks.RestoreWorkflow]: getWorkflowCb,
	[Tasks.InstallInstallableVersion]: (payload: ProcessTaskResponseInfo) => {
		if (!isGetInstallInstallableCbPayload(payload)) {
			throw new Error('Failed to get the publish workflow process status');
		}
		const { new_workflow_id: workflowId } = payload;
		return getWorkflow(workflowId);
	},
	[Tasks.DuplicateWorkflow]: (payload: ProcessTaskResponseInfo) => {
		if (!isGetWorkflowDuplicateCbPayload(payload)) {
			throw new Error('Failed to get the publish workflow process status');
		}
		const { new_workflow_id: workflowId } = payload;
		return getWorkflow(workflowId);
	},
	[Tasks.CreateSubflowFromNodes]: async (payload: ProcessTaskResponseInfo) => {
		if (!isGetCreateSubflowFromNodesCbPayload(payload)) {
			throw new Error('Failed to get the publish workflow process status');
		}
		return payload;
	},
};

const TIME_TO_WAIT = 2000;
const MAX_TRIES = 40;
const MAX_CONSECUTIVE_FAILED_TRIES = 3;

const waitForTaskToResolve = async (taskId: string) => {
	let resp: ProcessTaskResponse | null = null;
	let tries = 0;
	let failedTries = 0;
	let finalizedStatus = false;
	do {
		// wait for set time before next try
		!!resp && (await new Promise((resolve) => setTimeout(resolve, TIME_TO_WAIT)));
		tries++;
		try {
			resp = await getTask(taskId);
			failedTries = 0;
			finalizedStatus = isFinalizedStatus(resp.status);
		} catch (e) {
			// we don't want to fail the whole process if we failed to get the task status only once or twice, so we'll try again
			failedTries++;
			console.warn(`Failed to get the publish workflow process status, try #${tries} failed try #${failedTries}`, e);
		}
	} while (!finalizedStatus && tries < MAX_TRIES && failedTries < MAX_CONSECUTIVE_FAILED_TRIES);

	if (!finalizedStatus || !resp || !isFinalizeProcessTaskResponse(resp)) {
		throw new Error('Failed to get the task status');
	}

	return resp;
};

export const useTaskResolver = () => ({
	dispatchAndWaitTask: async (taskName: Tasks, payload: Record<string, unknown>) => {
		const { task_id: taskId } = await dispatchTask(taskName, payload);
		const task = await waitForTaskToResolve(taskId);
		if (isErrorProcessTaskResponse(task)) {
			const error = task.error;
			throw new TaskProcessError(error.detail, error.code, error.metadata);
		}
		if (!isSuccessProcessTaskResponse(task)) {
			throw new Error('Failed to get the task status');
		}
		const getEntity = GET_ENTITIES_CB[taskName];
		return getEntity(task.info);
	},
});
